import React, { useState } from 'react'
import PriceValidationUtils from '../../../utils/priceValidation'

interface BatchPriceUpdateProps {
  onApply: (priceValue: number) => void
  currency?: string
  marketplace?: string
}

const BatchPriceUpdate: React.FC<BatchPriceUpdateProps> = ({ 
  onApply, 
  currency = 'IDR',
  marketplace = 'shopee'
}) => {
  const [batchValue, setBatchValue] = useState<string>('')
  const [validationError, setValidationError] = useState<string>('')

  const handleApply = () => {
    const value = parseFloat(batchValue)
    if (isNaN(value) || value < 0) {
      setValidationError('Please enter a valid price')
      return
    }

    // Validate price for marketplace
    const validation = PriceValidationUtils.validateMarketplacePrice(marketplace, value)
    if (!validation.valid) {
      setValidationError(validation.error || 'Invalid price')
      return
    }

    // Clear validation error and apply value
    setValidationError('')
    onApply(value)
    setBatchValue('')
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    
    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError('')
    }
    
    // Allow empty string or valid decimal numbers with up to 2 decimal places
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setBatchValue(value)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleApply()
    }
  }

  const getCurrencySymbol = (currency: string) => {
    switch (currency.toLowerCase()) {
      case 'usd':
        return '$'
      case 'idr':
        return 'Rp'
      case 'sgd':
        return 'S$'
      case 'myr':
        return 'RM'
      default:
        return currency
    }
  }

  const getCurrencyPlaceholder = (currency: string) => {
    switch (currency.toLowerCase()) {
      case 'usd':
        return '0.00'
      case 'idr':
        return 'e.g. 3.000.000'
      case 'sgd':
        return '0.00'
      case 'myr':
        return '0.00'
      default:
        return '0.00'
    }
  }

  const getMarketplaceInfo = () => {
    return PriceValidationUtils.getMarketplaceDisplayInfo(marketplace)
  }

  const marketplaceInfo = getMarketplaceInfo()
  const hasValidationError = !!validationError

  return (
    <div
      className="tw-p-4 tw-rounded-lg tw-bg-gray-50 tw-border tw-border-gray-200"
      data-testid="batch-update-section"
    >
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
        <h3
          className="tw-font-semibold tw-text-gray-800"
          data-testid="batch-update-title"
        >
          Batch Update
        </h3>
        <div className="tw-text-xs tw-text-gray-600">
          <span className="tw-font-medium">{marketplaceInfo.name}</span> • Min: {marketplaceInfo.minPrice}
        </div>
      </div>

      <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-4">
        <div className="tw-flex-1 tw-min-w-[180px]">
          <label
            className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1"
            htmlFor="batch-new-price"
            data-testid="batch-price-label"
          >
            Regular Price ({getCurrencySymbol(currency)})
          </label>
          <input
            id="batch-new-price"
            type="number"
            min="0"
            step="0.01"
            placeholder={getCurrencyPlaceholder(currency)}
            value={batchValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            className={`form-input tw-w-full tw-rounded-md tw-shadow-sm focus:tw-ring-primary tw-text-sm ${
              hasValidationError
                ? 'tw-border-red-300 focus:tw-border-red-500 tw-bg-red-50'
                : 'tw-border-gray-300 focus:tw-border-primary'
            }`}
            data-testid="batch-price-input"
          />

          {hasValidationError && (
            <div className="tw-mt-1 tw-text-xs tw-text-red-600 tw-flex tw-items-center tw-gap-1">
              <span className="tw-inline-block tw-w-3 tw-h-3">⚠️</span>
              {validationError}
            </div>
          )}
        </div>

        <div className="tw-flex-shrink-0">
          <button
            onClick={handleApply}
            disabled={!batchValue || parseFloat(batchValue) < 0 || hasValidationError}
            className="tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-h-10 tw-px-4 tw-bg-primary tw-text-white tw-text-sm tw-font-bold hover:tw-bg-primary/90 disabled:tw-bg-primary/40 disabled:tw-cursor-not-allowed tw-transition-colors"
            data-testid="apply-batch-button"
          >
            <span className="tw-truncate">Apply to All</span>
          </button>
        </div>
      </div>
      
      <div className="tw-mt-3 tw-text-xs tw-text-gray-500 tw-space-y-1">
        <p>
          • This price will be applied to all product variants
        </p>
        <p>
          • Prices are validated against {marketplaceInfo.name} requirements
        </p>
        <p>
          • Leave individual variant prices empty to skip updates
        </p>
      </div>
    </div>
  )
}

export default BatchPriceUpdate